import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { ABLE_COLORS } from '../../../constants';
import TaskItem from './TaskItem';

/**
 * PlanModeList - Task list with add input for Plan mode
 * 
 * Features:
 * - Add task input with Enter key support
 * - Task list with inline editing
 * - Empty state message
 */
export default function PlanModeList({
  tasks,
  filteredTasks,
  onAddTask,
  onMarkDone,
  onMarkUndone,
  onToggleStar,
  onUpdateTask,
  onDeleteTask,
  contextOptions
}) {
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [editingTaskId, setEditingTaskId] = useState(null);

  const handleAddTask = () => {
    if (newTaskTitle.trim()) {
      onAddTask(newTaskTitle.trim());
      setNewTaskTitle('');
    }
  };

  return (
    <div style={{ width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
      {/* Add task input */}
      <div style={{
        background: ABLE_COLORS.neutral,
        borderRadius: ABLE_COLORS.cardRadiusLarge,
        padding: '16px',
        marginBottom: '16px',
        boxShadow: ABLE_COLORS.cardShadow,
        border: `1px solid ${ABLE_COLORS.borderLight}`,
        display: 'flex',
        gap: '10px'
      }}>
        <input
          type="text"
          value={newTaskTitle}
          onChange={(e) => setNewTaskTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAddTask();
          }}
          placeholder="Add a task..."
          style={{
            flex: 1,
            padding: '12px 14px',
            border: `1px solid ${ABLE_COLORS.border}`,
            borderRadius: ABLE_COLORS.chipRadius,
            fontSize: '14px',
            outline: 'none',
            transition: 'border-color 0.15s ease'
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = ABLE_COLORS.primary;
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = ABLE_COLORS.border;
          }}
        />
        <button
          onClick={handleAddTask}
          disabled={!newTaskTitle.trim()}
          style={{
            padding: '12px 16px',
            background: newTaskTitle.trim() ? ABLE_COLORS.primary : ABLE_COLORS.border,
            color: 'white',
            border: 'none',
            borderRadius: ABLE_COLORS.chipRadius,
            cursor: newTaskTitle.trim() ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'all 0.15s ease'
          }}
        >
          <Plus size={18} />
        </button>
      </div>

      {/* Task list */}
      <div style={{
        background: ABLE_COLORS.neutral,
        borderRadius: ABLE_COLORS.cardRadiusLarge,
        padding: '16px',
        boxShadow: ABLE_COLORS.cardShadow,
        border: `1px solid ${ABLE_COLORS.borderLight}`
      }}>
        <h3 style={{
          margin: '0 0 12px 0',
          fontSize: '13px',
          fontWeight: '600',
          color: ABLE_COLORS.textMuted,
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          Tasks ({filteredTasks.length})
        </h3>

        {filteredTasks.length === 0 ? (
          <p style={{
            textAlign: 'center',
            color: ABLE_COLORS.textMuted,
            fontSize: '14px',
            padding: '24px 0'
          }}>
            No tasks yet. Add one above!
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filteredTasks.map(task => (
              <TaskItem
                key={task.id}
                task={task}
                tasks={tasks}
                isEditing={editingTaskId === task.id}
                onStartEdit={() => setEditingTaskId(task.id)}
                onCancelEdit={() => setEditingTaskId(null)}
                onMarkDone={onMarkDone}
                onMarkUndone={onMarkUndone}
                onToggleStar={onToggleStar}
                onUpdateTask={onUpdateTask}
                onDeleteTask={onDeleteTask}
                contextOptions={contextOptions}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
